// This is a quick solution for getting the mapper working with snowflake
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Tooltip,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import modalStyles from '../../DataBuilder/data-builder-modals.module.css';
import { fetchSnowflakeColumns } from '../company-selection/company-mapping/upload-companies/snowflake-to-company-mapping';
import {
  SnowflakeDbInputs,
  type SnowflakeDbValues,
} from '../snowflake-db-inputs';
import {
  type SnowflakeDataInterface,
  useSnowflakeMapperStore,
} from './snowflake-mapper-context';

export const SnowflakeInputsWithContext = () => {
  const setData = useSnowflakeMapperStore((state) => state.setData);
  const goToNext = useSnowflakeMapperStore((state) => state.goToNext);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<SnowflakeDbValues>();
  const [apiError, setApiError] = useState<string | null>(null);
  const submitSnowflakeCompanyMapping = handleSubmit(
    async (formData: SnowflakeDbValues) => {
      try {
        setApiError(null);
        const data = await fetchSnowflakeColumns({ ...formData });
        setData((prevData: SnowflakeDataInterface) => ({
          ...prevData,
          headers: data.columns,
          combinedData: data.columns.map((col: string) => ({
            header: col,
            sampleData: Object.values(data.data[col]) || [],
          })),
          formData: formData,
        }));
        goToNext();
        // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
      } catch (error) {
        setApiError(
          "Something didn't go as planned. Please check for typos and try again."
        );
      }
    }
  );

  return (
    <Box>
      <form onSubmit={submitSnowflakeCompanyMapping}>
        <Heading
          className={modalStyles.modalInkText}
          color="text.primary"
          fontWeight="600"
          size="sm"
          mb="4"
        >
          <Flex alignItems="center" gap={1}>
            Snowflake table
            <Tooltip
              label='Supported columns: "COMPANY_ID", "COMPANY_NAME", "TICKER", "WEBSITE", "YEAR_FOUNDED", "COUNTRY", "STATE", "CITY", "LINKEDIN_URL", "FACTSET_ENTITY_ID", "LEI", "CIK", "SIK", "NAICS", "ISIN"'
              textAlign="center"
              hasArrow
              placement="top"
            >
              <InfoOutlineIcon
                className={modalStyles.modalInkText}
                boxSize={3}
                color="text.primary"
                cursor="pointer"
              />
            </Tooltip>
          </Flex>
        </Heading>
        <Flex alignItems="flex-end">
          <SnowflakeDbInputs
            testIdPrefix="snowflakeMapper"
            register={register}
            errors={errors}
          />

          <Button
            className={modalStyles.footerPrimaryButton}
            colorScheme="green"
            variant="solid"
            size="md"
            isLoading={isSubmitting}
            type="submit"
            w="130px"
          >
            Submit
          </Button>
        </Flex>

        {apiError && (
          <Alert status="error" mt="2">
            <AlertIcon />
            {apiError}
          </Alert>
        )}
      </form>
    </Box>
  );
};
